/**
 * MUI Button component that replaces the styled-components Button
 * 
 * This component provides a Material UI equivalent of the original Button component
 * with similar props and functionality. It uses the ThemeAdapter to handle legacy themes.
 */
import React from 'react';
import { Button as MuiButtonBase, styled, Box, Tooltip, TooltipProps } from '@mui/material';
import { adaptButtonTheme, adaptIconTheme } from 'theme/ThemeAdapter';
import { IconSprite } from 'components/atoms';
import icons from 'globalConstants/Icons';
import { ButtonProps } from 'types/components/molecules';

/**
 * MUI Button component
 * 
 * @param props - Component props
 * @returns MUI Button component
 */
const Button: React.FC<ButtonProps> = (props) => {
  const {
    variant,
    size,
    disabled,
    onClick,
    onMouseDown,
    onMouseUp,
    children,
    label,
    name,
    theme,
    state,
    editable,
    bubble,
    place,
    style,
    useIcon,
    usage,
    ...otherProps
  } = props;
  
  // Check if we should show an icon
  const hasIcon = (useIcon ?? true) && name && name in icons;
  const vertical = theme?.vertical ?? false;
  const upperCase = theme?.uppercase ?? false;
  
  // Adapt legacy theme to MUI props
  const adaptedProps = adaptButtonTheme(theme, {
    editable,
    variant,
    size,
    disabled,
    onClick,
    onMouseDown,
    onMouseUp,
    id: props.id,
    className: props.className,
    ...otherProps
  });
  
  // Ensure critical props are passed through even if adaptButtonTheme doesn't handle them
  const buttonProps = {
    ...adaptedProps,
    disabled,
    onClick,
    onMouseDown,
    onMouseUp,
    id: props.id,
    className: props.className,
    ...otherProps
  };
  
  // Get icon theme if state is provided
  const iconThemeProps = state ? adaptIconTheme(state) : {};
  
  // Create the button content
  const buttonContent = (
    <Box className="d-flex align-items-center" width="100%">
      {label && editable && (
        <span className="align-items-center text-truncate">
          {label.toString().replace('_', ' ')}
        </span>
      )}
      
      {hasIcon && (
        <IconSprite 
          state={state} 
          icon={name as string} 
          size={theme?.iconSize || '34px'} 
          scale={theme?.iconScale || '1'} 
          usage={usage}
          {...iconThemeProps}
        />
      )}
      
      {label && !editable && (
        <span className={`
          d-inline-flex align-items-center 
          ${hasIcon ? (vertical ? 'w-100 ms-0 justify-content-center' : 'w-100 ms-2') : 'justify-content-center'}
          ${upperCase ? 'text-uppercase' : ''}`}
        >
          {label.toString().replace('_', ' ')}
        </span>
      )}
      
      {children}
    </Box>
  );
  
  // Wrap with tooltip if bubble is provided
  if (bubble) {
    // Need to cast the placement to TooltipProps['placement'] to avoid type errors
    const tooltipPlacement = place as TooltipProps['placement'] || 'top';
    
    return (
      <Tooltip title={bubble} placement={tooltipPlacement}>
        <StyledButton {...buttonProps} sx={style} disableRipple>
          {buttonContent}
        </StyledButton>
      </Tooltip>
    );
  }
  
  return (
    <StyledButton {...buttonProps} sx={style} disableRipple>
      {buttonContent}
    </StyledButton>
  );
};

// Styled MUI Button component - allow full custom styling override with disabled animations
const StyledButton = styled(MuiButtonBase)(() => ({
  textTransform: 'none',
  borderRadius: '4px',
  fontWeight: 500,
  padding: '6px 12px',
  minWidth: 'auto',
  // Disable all animations and ripple effects
  transition: 'none !important',
  animation: 'none !important',
  '& .MuiTouchRipple-root': {
    display: 'none !important'
  },
  '& .MuiTouchRipple-child': {
    display: 'none !important'
  },
  '&:hover': {
    transition: 'none !important',
    animation: 'none !important',
    transform: 'none !important'
  },
  '&:active': {
    transition: 'none !important',
    animation: 'none !important',
    transform: 'none !important'
  },
  '&:focus': {
    transition: 'none !important',
    animation: 'none !important',
    transform: 'none !important',
    outline: 'none !important'
  },
  // No default contained or outlined styles - let sx prop handle everything
}));

export type { ButtonProps };
export default Button;
